import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import FormBuilder from '../../../../../../../shared/components/form-builder/form-builder';
import { IAdditionalInfo, normalizeStringArray } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useFormActions } from '../../../../../../../shared/hooks/form-builder.hooks';
import { useTransactionCertificateStore } from '../../../../../../../store/transaction-certificates.store';
import useNotification from '../../../../../../../shared/hooks/useNotification';
import { buyerSellerDetailsService } from '../../../../tc-service/buyer-seller-details.service';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../../../shared/hooks/useApi';
import { TCAppConfig } from '../../../../../../../shared/app-configs/tc-app-configs';

interface AdditionalInfoProps {
  formRef: React.MutableRefObject<any>;
  additionalInfoData: IAdditionalInfo;
  setAdditionalInfoData: React.Dispatch<React.SetStateAction<IAdditionalInfo>>;
  hasRecycledClaim?: boolean;
  productCategoryCode?: string;
}

interface AdditionalInfoFormValues extends IAdditionalInfo {
  tcProductOriginFacilityTeIdSearch: string;
}

interface TEDetail {
  TE_ID: string;
  [key: string]: any;
}

// Allowed PR codes when hasRecycledClaim is true (GRS/RCS standards)
const ALLOWED_RECLAIMED_MATERIAL_CODES = [
  "PR0035", "PR0025", "PR0005", "PR0027", "PR0008",
  "PR0012", "PR0033", "PR0015", "PR0021", "PR0016", "PR0052"
];

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  formRef,
  additionalInfoData,
  setAdditionalInfoData,
  productCategoryCode = "",
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("additional-Info");
  const { getTCStandardGOTS } = useTransactionCertificateStore();
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [tcStandard, setTcStandard] = useState<string[]>([]);

  const initialValues: AdditionalInfoFormValues = {
    ...additionalInfoData,
    tcProductFeedstock: Array.isArray(additionalInfoData.tcProductFeedstock)
      ? additionalInfoData.tcProductFeedstock
      : additionalInfoData.tcProductFeedstock && String(additionalInfoData.tcProductFeedstock).trim()
        ? [String(additionalInfoData.tcProductFeedstock)]
        : [],
    tcProductReclaimedMaterialSource: Array.isArray(additionalInfoData.tcProductReclaimedMaterialSource)
      ? additionalInfoData.tcProductReclaimedMaterialSource
      : additionalInfoData.tcProductReclaimedMaterialSource && String(additionalInfoData.tcProductReclaimedMaterialSource).trim()
        ? [String(additionalInfoData.tcProductReclaimedMaterialSource)]
        : [],
    tcProductPackagingFor: normalizeStringArray(additionalInfoData.tcProductPackagingFor),
    tcProductOriginFacilityTeId: Array.isArray(additionalInfoData.tcProductOriginFacilityTeId)
      ? additionalInfoData.tcProductOriginFacilityTeId
      : additionalInfoData.tcProductOriginFacilityTeId
        ? [String(additionalInfoData.tcProductOriginFacilityTeId)]
        : [],
    tcProductOriginFacilityTeIdSearch: "",
  };

  const fetchTEDetails = async (
    query: string,
    offset: number
  ): Promise<TEDetail[]> => {
    if (!query || query.length < 3) {
      return [];
    }
    try {
      const queryParams: Record<string, string | number> = {
        query_param: query,
        offset,
        name: "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response?.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };
  useEffect(() => {
    const fetchTcBaseDetails = async () => {
      if (!TE_ID) return;
      try {
        const response = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID });
        const standards = response?.data?.tcStandard || [];
        setTcStandard(Array.isArray(standards) ? standards : []);
      } catch (err) {
        console.error("Failed to fetch TC base details:", err);
      }
    };
    fetchTcBaseDetails();
  }, [TE_ID]); // executeGet is stable from useApi hook, no need to include in deps

  // Check if tcStandard includes GRS or RCS
  const hasGRSorRCS = tcStandard.includes("GRS") || tcStandard.includes("RCS");
  const hasRecycledClaim = hasGRSorRCS;

  const formConfig = {
    pageName: "additional-Info",
    subPageName: "tcProductAdditionalInfo",
    initialValues,
    validationSchema: Yup.object().shape({
      tcProductOriginFacilityTeId: Yup.array()
        .of(Yup.string().max(255, "Each TE-ID must be at most 255 characters"))
        .nullable(),
      tcProductOriginFacilityTeIdSearch: Yup.string().nullable(),
      tcProductInfoFiberLength: Yup.string()
        .when([], {
          is: () => {
            const fiberCategories = ["PC0032", "PC0033", "PC0034"];
            return getTCStandardGOTS || fiberCategories.includes(productCategoryCode);
          },
          then: Yup.string().required("Fiber Length is required"),
          otherwise: Yup.string().nullable(),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoYarnCount: Yup.string()
        .when([], {
          is: () => {
            const yarnCategories = ["PC0029", "PC0030", "PC0031"];
            return yarnCategories.includes(productCategoryCode);
          },
          then: Yup.string().required("Yarn Count is required"),
          otherwise: Yup.string().nullable(),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoFabricWeightAndConstruct: Yup.string()
        .when([], {
          is: () => {
            const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
            return fabricCategories.includes(productCategoryCode);
          },
          then: Yup.string().required("Fabric Weight and Construct is required"),
          otherwise: Yup.string().nullable(),
        })
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoFabricWeightOfFinishedProduct: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductGtin: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductInfoAdditional: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductAdditives: Yup.array()
        .of(Yup.string().max(255, "Each additive must be at most 255 characters"))
        .when("tcProductAdditivesWeight", {
          is: (val: any) => {
            if (val === undefined || val === null) return false;
            const str = String(val).trim();
            if (!str) return false;
            // treat '0' or 0 as empty (only require additives when meaningful weight provided)
            const num = Number(str);
            if (!isNaN(num) && num === 0) return false;
            return true;
          },
          then: Yup.array()
            .of(Yup.string().max(255, "Each additive must be at most 255 characters"))
            .min(1, "Product Additives is required when Product Additives Weight is provided")
            .required("Product Additives is required when Product Additives Weight is provided"),
          otherwise: Yup.array()
            .of(Yup.string().max(255, "Each additive must be at most 255 characters"))
            .nullable(),
        }),
      tcProductInfoTechnicalSpecifications: Yup.string()
        .nullable()
        .max(255, "Maximum 255 characters allowed"),
      tcProductAdditivesWeight: Yup.string()
        .nullable()
        .test(
          "is-max-five-decimal-places",
          "Product Additives Weight : Must be a valid decimal number with up to 5 decimal places",
          (value: string | null | undefined) => {
            if (!value) return true;
            return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(value);
          }
        )
        .max(255, "Maximum 255 characters allowed"),
      tcProductFeedstock: Yup.array()
        .of(Yup.string().max(255, "Each feedstock entry must be at most 255 characters"))
        .when([], {
          is: () => Boolean(hasRecycledClaim),
          then: Yup.array()
            .of(Yup.string().max(255, "Each feedstock entry must be at most 255 characters"))
            .min(1, "Feedstock is required when product claims include recycled material (GRS/RCS)")
            .required("Feedstock is required when product claims include recycled material (GRS/RCS)"),
          otherwise: Yup.array()
            .of(Yup.string().max(255, "Each feedstock entry must be at most 255 characters"))
            .nullable(),
        }),
      tcProductPercentTextileWaste: Yup.string()
        .nullable()
        .test(
          "is-max-five-decimal-places",
          "ProductPercentTextileWaste : Must be a valid decimal number with up to 5 decimal places",
          (value: string | null | undefined) => {
            if (!value) return true;
            return /^(0|[1-9]\d*|0\.\d{1,5}|[1-9]\d*\.\d{1,5})$/.test(value);
          }
        )
        .max(255, "Maximum 255 characters allowed"),
      tcProductPackagingFor: Yup.array()
        .of(Yup.string().max(100, "Each product packaging for entry must be at most 100 characters"))
        .nullable(),
      tcProductReclaimedMaterialSource: Yup.array()
        .of(Yup.string()
          .test(
            "is-valid-pr-code",
            "ProductReclaimedMaterialSource : Invalid code. Expected format PR followed by 4 digits (e.g. PR0023) or enter 4 digits and we will prefix 'PR' automatically",
            (value: string | null | undefined) => {
              if (!value) return true;
              const v = String(value).trim();
              const prPattern = /^PR\d{4}$/i;
              const digitsPattern = /^\d{4}$/;
              return prPattern.test(v) || digitsPattern.test(v);
            }
          )
          .max(255, "Each reclaimed material source must be at most 255 characters")
        )
        .when([], {
          is: () => Boolean(hasRecycledClaim),
          then: Yup.array()
            .of(Yup.string()
              .test(
                "is-valid-pr-code",
                "ProductReclaimedMaterialSource : Invalid code. Expected format PR followed by 4 digits (e.g. PR0023) or enter 4 digits and we will prefix 'PR' automatically",
                (value: string | null | undefined) => {
                  if (!value) return false;
                  const v = String(value).trim();
                  const prPattern = /^PR\d{4}$/i;
                  const digitsPattern = /^\d{4}$/;
                  return prPattern.test(v) || digitsPattern.test(v);
                }
              )
              .test(
                "is-allowed-pr-code",
                "ProductReclaimedMaterialSource : This code is not allowed when product claims include recycled material (GRS/RCS). Allowed codes are: PR0035, PR0025, PR0005, PR0027, PR0008, PR0012, PR0033, PR0015, PR0021, PR0016, PR0052",
                (value: string | null | undefined) => {
                  if (!value) return false;
                  const v = String(value).trim().toUpperCase();
                  const normalized = v.startsWith("PR") ? v : `PR${v}`;
                  return ALLOWED_RECLAIMED_MATERIAL_CODES.includes(normalized);
                }
              )
              .max(255, "Each reclaimed material source must be at most 255 characters")
            )
            .min(1, "Reclaimed Material Source is required when product claims include recycled material (GRS/RCS)")
            .required("Reclaimed Material Source is required when product claims include recycled material (GRS/RCS)"),
          otherwise: Yup.array()
            .of(Yup.string()
              .test(
                "is-valid-pr-code",
                "ProductReclaimedMaterialSource : Invalid code. Expected format PR followed by 4 digits (e.g. PR0023) or enter 4 digits and we will prefix 'PR' automatically",
                (value: string | null | undefined) => {
                  if (!value) return true;
                  const v = String(value).trim();
                  const prPattern = /^PR\d{4}$/i;
                  const digitsPattern = /^\d{4}$/;
                  return prPattern.test(v) || digitsPattern.test(v);
                }
              )
              .max(255, "Each reclaimed material source must be at most 255 characters")
            )
            .nullable(),
        }),
    }),
    sections: [
      {
        title: 'Additional Information',
        fields: [
          {
            label: 'Product Info Fiber Length (mm)',
            name: 'tcProductInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (() => {
              const fiberCategories = ["PC0032", "PC0033", "PC0034"];
              return getTCStandardGOTS || fiberCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Fiber Length',
            },
          },
          {
            label: 'Product Info Yarn Count',
            name: 'tcProductInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (() => {
              const yarnCategories = ["PC0029", "PC0030", "PC0031"];
              return yarnCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Yarn Count',
            },
          },
          {
            label: 'Product Info Fabric Weight and Construct(kg)',
            name: 'tcProductInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (() => {
              const fabricCategories = ["PC0025", "PC0026", "PC0027", "PC0028", "PC0039"];
              return fabricCategories.includes(productCategoryCode);
            })(),
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
            },
          },
          {
            label: 'Product Info Fabric Weight of Finished Product (kg)',
            name: 'tcProductInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
            },
          },
          {
            label: 'Product Gtin',
            name: 'tcProductGtin',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: 'Enter Product Gtin',
            },
          },
          {
            label: 'Product Additional Info',
            name: 'tcProductInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: 'Enter Additional Info',
            },
          },
          {
            label: 'Product Additives',
            name: 'tcProductAdditives',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter Product Additives',
            },
          },
          {
            label: 'Product Additives Weight(kg)',
            name: 'tcProductAdditivesWeight',
            type: 'number',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              type: 'number',
              placeholder: 'Enter Product Additives Weight',
            },
          },
          {
            label: 'Product Info Technical Specifications',
            name: 'tcProductInfoTechnicalSpecifications',
            type: 'text',
            gridSize: { xs: 12, md: 8, lg: 8 },
            required: false,
            props: {
              placeholder: 'Enter Product Technical Specifications',
            },
          },

          {
            label: "Product Feed Stock",
            name: 'tcProductFeedstock',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: hasRecycledClaim,
            props: {
              placeholder: 'Enter Product Feed Stock entries',
            },
          },
          {
            label: 'Product Percent Textile Waste',
            name: 'tcProductPercentTextileWaste',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              type: 'text',
              placeholder: 'Enter Product Percent Waste',
            },
          },
          {
            label: 'Product Packaging For',
            name: 'tcProductPackagingFor',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              placeholder: 'Enter Product Packaging For',
            },
          },
          {
            label: "Product Material Reclaimed Source",
            name: 'tcProductReclaimedMaterialSource',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: hasRecycledClaim,
            props: {
              placeholder: "Enter codes like 'PR0023' or just '0023'",
            },
          },
          {
            label: 'Product Origin Facility TE-ID',
            name: 'tcProductOriginFacilityTeId',
            type: 'chips',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            props: {
              disableManualEntry: true,
            },
          },
          {
            label: '',
            name: 'tcProductOriginFacilityTeIdSearch',
            type: 'search',
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            events: {
              onChange: (
                selected: TEDetail | null,
                values: AdditionalInfoFormValues,
                setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
              ) => {
                if (selected && selected.TE_ID) {
                  const existing = Array.isArray(values.tcProductOriginFacilityTeId)
                    ? values.tcProductOriginFacilityTeId
                    : values.tcProductOriginFacilityTeId
                      ? [String(values.tcProductOriginFacilityTeId)]
                      : [];
                  if (!existing.includes(selected.TE_ID)) {
                    setFieldValue("tcProductOriginFacilityTeId", [
                      ...existing,
                      selected.TE_ID,
                    ]);
                  }
                }
                values.tcProductOriginFacilityTeIdSearch = "";
                setFieldValue("tcProductOriginFacilityTeIdSearch", "", false);
              },
            },
            props: {
              pageName: "Product Origin Facility TE-ID",
              placeholder: "Search for the TE-ID",
              displayKey: "TE_ID",
              restrictInputFormat: true,
              clearInputOnSelect: true,
              onSearch: fetchTEDetails,
            },
          },

        ],
      },
    ],
    handleSubmit: async (values: AdditionalInfoFormValues) => {
      try {

        const { tcProductOriginFacilityTeIdSearch, ...rest } = values;

        // Normalize reclaimed material source array to expected PR###### format
        const normalized = { ...rest } as any;

        if (Array.isArray(normalized.tcProductReclaimedMaterialSource)) {
          normalized.tcProductReclaimedMaterialSource = normalized.tcProductReclaimedMaterialSource.map((item: string) => {
            const raw = String(item).trim();
            const digitsPattern = /^\d{4}$/;
            const prPattern = /^PR(\d{4})$/i;
            if (digitsPattern.test(raw)) {
              return `PR${raw}`;
            } else if (prPattern.test(raw)) {
              const m = raw.match(prPattern);
              return `PR${m ? m[1] : raw.slice(2)}`;
            } else {
              return raw;
            }
          });
        }

        // Ensure feedstock is always an array
        if (!Array.isArray(normalized.tcProductFeedstock)) {
          normalized.tcProductFeedstock = normalized.tcProductFeedstock && String(normalized.tcProductFeedstock).trim() ? [String(normalized.tcProductFeedstock)] : [];
        }
        normalized.tcProductPackagingFor = normalizeStringArray(normalized.tcProductPackagingFor);

        const sanitizedData: IAdditionalInfo = {
          ...normalized,
          tcProductOriginFacilityTeId: Array.isArray(normalized.tcProductOriginFacilityTeId)
            ? Array.from(new Set(normalized.tcProductOriginFacilityTeId))
            : [],
        };

        setAdditionalInfoData(sanitizedData);

        return sanitizedData;
      } catch (error) {
        console.error("Error saving additional info:", error);
        throw error;
      }
    },
  };
  

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default AdditionalInfo;
